import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Car, FileText, AlertCircle } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const EditVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    fleetSysId: '',
    fleetSysVehicleId: '',
    fuel: 'PETROL',
    make: '',
    model: '',
    type: 'SEDAN',
    year: '',
    color: '',
    regNo: '',
    insuranceExpiry: '',
    roadTaxExpiry: '',
    fitnessExpiry: '',
    pucExpiry: '',
    permitExpiry: '',
    permitNumber: '',
    vendorId: '',
    regionalOffice: '',
    isParivahanFailed: false
  });

  const [originalFormData, setOriginalFormData] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [documents, setDocuments] = useState({
    roadTaxDoc: null,
    permitDoc: null,
    fitnessDoc: null,
    pucDoc: null,
    insurance: null,
    vehicleRcDoc: null,
    vehiclePhoto: null
  });

  const [existingDocuments, setExistingDocuments] = useState({
    roadTaxDoc: null,
    permitDoc: null,
    fitnessDoc: null,
    pucDoc: null,
    insurance: null,
    vehicleRcDoc: null,
    vehiclePhoto: null
  });

  // Fetch vehicle details
  const { data: vehicleData, isLoading, error } = useQuery(
    ['vehicle', id],
    () => api.get(`/vehicles/${id}`),
    { enabled: !!id }
  );

  // Fetch vendors for dropdown
  const { data: vendorsData } = useQuery(
    ['vendors', 'all'],
    () => api.get('/vendors', { params: { limit: 1000, isApproved: 'true' } }),
    { staleTime: 300000 }
  );

  // Fetch regional offices for dropdown
  const { data: regionalOfficesData } = useQuery(
    ['regional-offices', 'all'],
    () => api.get('/regional-offices', { params: { limit: 1000 } }),
    { staleTime: 300000 }
  );

  // Log data for debugging
  // useEffect(() => {
  //   if (vehicleData?.data) {
  //     console.log('Vehicle Data:', vehicleData.data);
  //   }
  // }, [vehicleData]);

  // useEffect(() => {
  //   if (vendorsData?.data) {
  //     console.log('Vendors Data:', vendorsData.data);
  //   }
  // }, [vendorsData]);

  // useEffect(() => {
  //   if (regionalOfficesData?.data) {
  //     console.log('Full Regional Offices Response:', regionalOfficesData);
  //     console.log('Regional Offices Data:', regionalOfficesData.data);
  //     
  //     // Try multiple paths to extract offices
  //     let offices = null;
  //     
  //     // Path 1: data.offices
  //     if (regionalOfficesData.data.offices) {
  //       offices = regionalOfficesData.data.offices;
  //       console.log('Found offices at data.offices');
  //     }
  //     // Path 2: data.regionalOffices
  //     else if (regionalOfficesData.data.regionalOffices) {
  //       offices = regionalOfficesData.data.regionalOffices;
  //       console.log('Found offices at data.regionalOffices');
  //     }
  //     // Path 3: data.data.offices (nested)
  //     else if (regionalOfficesData.data.data?.offices) {
  //       offices = regionalOfficesData.data.data.offices;
  //       console.log('Found offices at data.data.offices');
  //     }
  //     // Path 4: data.data.regionalOffices (nested)
  //     else if (regionalOfficesData.data.data?.regionalOffices) {
  //       offices = regionalOfficesData.data.data.regionalOffices;
  //       console.log('Found offices at data.data.regionalOffices');
  //     }
  //     
  //     console.log('Extracted offices:', offices);
  //     console.log('Number of offices:', offices?.length);
  //   }
  // }, [regionalOfficesData]);

  // useEffect(() => {
  //   if (vendorsData?.data?.vendors) {
  //     const activeVendors = vendorsData.data.vendors.filter(v => v.isDeleted === false);
  //   }
  // }, [vendorsData]);

  // Update form data when vehicle data is loaded
  useEffect(() => {
    if (vehicleData?.data) {
      // Handle nested response structure {success: true, data: {...}}
      const vehicleResponse = vehicleData.data;
      const vehicle = vehicleResponse.data || vehicleResponse;
      
      // console.log('Vehicle response:', vehicleResponse);
      // console.log('Actual vehicle data:', vehicle);
      
      const initialFormData = {
        fleetSysId: vehicle.fleetSysId || '',
        fleetSysVehicleId: vehicle.fleetSysVehicleId || '',
        fuel: vehicle.fuelType || vehicle.fuel || 'PETROL',
        make: vehicle.make || '',
        model: vehicle.model || '',
        type: vehicle.type || 'SEDAN',
        year: vehicle.year ? vehicle.year.toString() : '',
        color: vehicle.color || '',
        regNo: vehicle.regNo || '',
        insuranceExpiry: vehicle.insuranceExpiry ? new Date(vehicle.insuranceExpiry).toISOString().split('T')[0] : '',
        roadTaxExpiry: vehicle.roadTaxExpiry ? new Date(vehicle.roadTaxExpiry).toISOString().split('T')[0] : '',
        fitnessExpiry: vehicle.fitnessExpiry ? new Date(vehicle.fitnessExpiry).toISOString().split('T')[0] : '',
        pucExpiry: vehicle.pucExpiry ? new Date(vehicle.pucExpiry).toISOString().split('T')[0] : '',
        permitExpiry: vehicle.permitExpiry ? new Date(vehicle.permitExpiry).toISOString().split('T')[0] : '',
        permitNumber: vehicle.permitNumber || '',
        vendorId: vehicle.vendorId?._id || vehicle.vendorId || '',
        regionalOffice: vehicle.regionalOffice?._id || vehicle.regionalOffice || '',
        isParivahanFailed: vehicle.isParivahanFailed || false
      };
      
      // console.log('Setting form data:', initialFormData);
      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
      
      // Set existing documents
      if (vehicle.documents) {
        // console.log('Setting documents:', vehicle.documents);
        setExistingDocuments({
          roadTaxDoc: vehicle.documents.roadTaxDoc || null,
          permitDoc: vehicle.documents.permitDoc || null,
          fitnessDoc: vehicle.documents.fitnessDoc || null,
          pucDoc: vehicle.documents.pucDoc || null,
          insurance: vehicle.documents.insurance || null,
          vehicleRcDoc: vehicle.documents.vehicleRcDoc || null,
          vehiclePhoto: vehicle.documents.vehiclePhoto || null
        });
      }
    }
  }, [vehicleData]);

  const updateMutation = useMutation(
    (data) => {
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(data).forEach(key => {
        if (key !== 'documents') {
          formDataToSend.append(key, data[key]);
        }
      });

      // Add documents
      Object.keys(documents).forEach(key => {
        if (documents[key]) {
          formDataToSend.append(key, documents[key]);
        }
      });

      return api.put(`/vehicles/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    {
      onSuccess: () => {
        toast.success('Vehicle updated successfully');
        navigate(`/vehicles/${id}`);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update vehicle');
      }
    }
  );

  const handleInputChange = (field, value) => {
    // console.log(`Field ${field} changed to:`, value);
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      // console.log('New form data:', newData);
      return newData;
    });
  };

  const isFieldEdited = (field) => {
    if (!originalFormData) return false;
    return originalFormData[field] !== formData[field];
  };

  const isDocumentEdited = (field) => {
    return documents[field] !== null;
  };

  const handleFileChange = (field, file) => {
    setDocuments(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);

    // Validation
    // if (!formData.fleetSysId.trim()) {
    //   toast.error('Fleet System ID is required');
    //   return;
    // }

    // if (!formData.fleetSysVehicleId.trim()) {
    //   toast.error('Fleet System Vehicle ID is required');
    //   return;
    // }

    if (!formData.regNo.trim()) {
      toast.error('Registration Number is required');
      return;
    }

    if (!formData.make.trim()) {
      toast.error('Make is required');
      return;
    }

    if (!formData.model.trim()) {
      toast.error('Model is required');
      return;
    }

    if (!formData.year.trim()) {
      toast.error('Year is required');
      return;
    }

    if (!formData.color.trim()) {
      toast.error('Color is required');
      return;
    }

    // Permit fields are now optional regardless of isParivahanFailed status

    // if (!formData.vendorId) {
    //   toast.error('Vendor is required');
    //   return;
    // }

    if (!formData.regionalOffice) {
      toast.error('Regional Office is required');
      return;
    }

    // Convert date strings to timestamps and map fuel to fuelType for backend
    const submitData = {
      ...formData,
      fuelType: formData.fuel, // Backend expects fuelType
      insuranceExpiry: formData.insuranceExpiry && formData.insuranceExpiry.trim() ? new Date(formData.insuranceExpiry).getTime() : undefined,
      roadTaxExpiry: formData.roadTaxExpiry && formData.roadTaxExpiry.trim() ? new Date(formData.roadTaxExpiry).getTime() : undefined,
      fitnessExpiry: formData.fitnessExpiry && formData.fitnessExpiry.trim() ? new Date(formData.fitnessExpiry).getTime() : undefined,
      pucExpiry: formData.pucExpiry && formData.pucExpiry.trim() ? new Date(formData.pucExpiry).getTime() : undefined,
      permitExpiry: formData.permitExpiry && formData.permitExpiry.trim() ? new Date(formData.permitExpiry).getTime() : undefined
    };

    // Remove fuel field since backend uses fuelType
    delete submitData.fuel;

    // Remove empty ObjectId fields to prevent cast errors
    if (!submitData.vendorId || submitData.vendorId.trim() === '') {
      delete submitData.vendorId;
    }

    // Remove permit fields if not Parivahan enabled
    if (!formData.isParivahanFailed) {
      delete submitData.permitNumber;
      delete submitData.permitExpiry;
    }

    updateMutation.mutate(submitData);
  };

  const getDocumentName = (url) => {
    if (!url) return null;
    const parts = url.split('/');
    return parts[parts.length - 1].split('?')[0];
  };

  const viewDocument = async (documentKey) => {
    if (existingDocuments[documentKey]) {
      try {
        const response = await api.get(`/vehicles/${id}/document/${documentKey}`);
        if (response.data?.data?.presignedUrl) {
          window.open(response.data.data.presignedUrl, '_blank');
        }
      } catch (error) {
        toast.error('Failed to load document');
      }
    }
  };

  const fuelOptions = [
    { value: 'PETROL', label: 'Petrol' },
    { value: 'DIESEL', label: 'Diesel' },
    { value: 'CNG', label: 'CNG' },
    { value: 'ELECTRIC', label: 'Electric' },
    { value: 'HYBRID', label: 'Hybrid' }
  ];

  const typeOptions = [
    { value: 'HATCHBACK', label: 'Hatchback' },
    { value: 'SEDAN', label: 'Sedan' },
    { value: 'SUV', label: 'SUV' },
    { value: 'MUV', label: 'MUV' },
    { value: 'LUXURY', label: 'Luxury' },
    { value: 'BIKE', label: 'Bike' },
    { value: 'AUTO', label: 'Auto' },
    { value: 'ELECTRIC_AUTO', label: 'Electric Auto' }
  ];

  // Debug: Log current form data
  // useEffect(() => {
  //   console.log('Current formData state:', formData);
  // }, [formData]);

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading vehicle details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="text-center py-8">
          <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Vehicle</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button onClick={() => navigate('/vehicles')} className="btn btn-primary">
            Back to Vehicles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/vehicles/${id}`)}
              className="btn btn-outline mr-4"
            >
              <ArrowLeft className="h-6 w-6 mr-2" />
              Back
            </button>
            <div className="page-title-icon">
              <Car className="h-6 w-6" />
            </div>
            <div>
              <h1 className="page-title">Edit Vehicle</h1>
              <p className="page-description">
                Update vehicle information
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Vehicle Information</h2>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmitClick} className="space-y-6">
            {/* Basic Information */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Fleet System ID</label>
                  <input
                    type="text"
                    value={formData.fleetSysId || ''}
                    onChange={(e) => handleInputChange('fleetSysId', e.target.value)}
                    className={`form-input ${isFieldEdited('fleetSysId') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter fleet system ID"
                    
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Fleet System Vehicle ID</label>
                  <input
                    type="text"
                    value={formData.fleetSysVehicleId || ''}
                    onChange={(e) => handleInputChange('fleetSysVehicleId', e.target.value)}
                    className={`form-input ${isFieldEdited('fleetSysVehicleId') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter fleet system vehicle ID"
                    
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Registration Number *</label>
                  <input
                    type="text"
                    value={formData.regNo || ''}
                    onChange={(e) => handleInputChange('regNo', e.target.value)}
                    className={`form-input ${isFieldEdited('regNo') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter registration number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Make *</label>
                  <input
                    type="text"
                    value={formData.make || ''}
                    onChange={(e) => handleInputChange('make', e.target.value)}
                    className={`form-input ${isFieldEdited('make') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter vehicle make"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Model *</label>
                  <input
                    type="text"
                    value={formData.model || ''}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    className={`form-input ${isFieldEdited('model') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter vehicle model"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Year *</label>
                  <input
                    type="text"
                    value={formData.year || ''}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                    className={`form-input ${isFieldEdited('year') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter vehicle year"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Color *</label>
                  <input
                    type="text"
                    value={formData.color || ''}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    className={`form-input ${isFieldEdited('color') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter vehicle color"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Fuel Type *</label>
                  <select
                    value={formData.fuel || 'PETROL'}
                    onChange={(e) => handleInputChange('fuel', e.target.value)}
                    className={`form-input ${isFieldEdited('fuel') ? 'border-2 border-blue-500' : ''}`}
                    required
                  >
                    {fuelOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Vehicle Type *</label>
                  <select
                    value={formData.type || 'SEDAN'}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className={`form-input ${isFieldEdited('type') ? 'border-2 border-blue-500' : ''}`}
                    required
                  >
                    {typeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Permit & Documents */}
            <div className="form-section">
              <h3 className="form-section-title">Permit & Documents</h3>

              <div className="form-grid">
                {/* Permit Fields - Always Optional */}
                <div className="form-group">
                  <label className="form-label">Permit Number <span className="text-gray-400">(Optional)</span></label>
                  <input
                    type="text"
                    value={formData.permitNumber || ''}
                    onChange={(e) => handleInputChange('permitNumber', e.target.value)}
                    className={`form-input ${isFieldEdited('permitNumber') ? 'border-2 border-blue-500' : ''}`}
                    placeholder="Enter permit number (optional)"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Permit Expiry <span className="text-gray-400">(Optional)</span></label>
                  <input
                    type="date"
                    value={formData.permitExpiry || ''}
                    onChange={(e) => handleInputChange('permitExpiry', e.target.value)}
                    className={`form-input ${isFieldEdited('permitExpiry') ? 'border-2 border-blue-500' : ''}`}
                  />
                </div>

              <div className="form-group">
                  <label className="form-label">Insurance Expiry</label>
                  <input
                    type="date"
                    value={formData.insuranceExpiry || ''}
                    onChange={(e) => handleInputChange('insuranceExpiry', e.target.value)}
                    className={`form-input ${isFieldEdited('insuranceExpiry') ? 'border-2 border-blue-500' : ''}`}
                    
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Fitness Expiry</label>
                  <input
                    type="date"
                    value={formData.fitnessExpiry || ''}
                    onChange={(e) => handleInputChange('fitnessExpiry', e.target.value)}
                    className={`form-input ${isFieldEdited('fitnessExpiry') ? 'border-2 border-blue-500' : ''}`}
                    
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">PUC Expiry</label>
                  <input
                    type="date"
                    value={formData.pucExpiry || ''}
                    onChange={(e) => handleInputChange('pucExpiry', e.target.value)}
                    className={`form-input ${isFieldEdited('pucExpiry') ? 'border-2 border-blue-500' : ''}`}
                    
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Road Tax Expiry</label>
                  <input
                    type="date"
                    value={formData.roadTaxExpiry || ''}
                    onChange={(e) => handleInputChange('roadTaxExpiry', e.target.value)}
                    className={`form-input ${isFieldEdited('roadTaxExpiry') ? 'border-2 border-blue-500' : ''}`}
                    
                  />
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div className="form-section">
              <h3 className="form-section-title">Assignment</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Vendor</label>
                  <select
                    value={formData.vendorId || ''}
                    onChange={(e) => handleInputChange('vendorId', e.target.value)}
                    className={`form-input ${isFieldEdited('vendorId') ? 'border-2 border-blue-500' : ''}`}
                    
                  >
                    <option value="">Select a vendor</option>
                    {vendorsData?.data?.vendors?.filter(v => v.isDeleted === false)?.map((vendor) => (
                      <option key={vendor._id} value={vendor._id}>
                        {vendor.VendorName} - {vendor.fleetSysId}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {vendorsData?.data?.vendors?.filter(v => v.isDeleted === false)?.length || 0} vendors available
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Regional Office *</label>
                  <select
                    value={formData.regionalOffice || ''}
                    onChange={(e) => handleInputChange('regionalOffice', e.target.value)}
                    className={`form-input ${isFieldEdited('regionalOffice') ? 'border-2 border-blue-500' : ''}`}
                    required
                  >
                    <option value="">Select a regional office</option>
                    {(() => {
                      const offices = regionalOfficesData?.data?.data?.offices || 
                                    regionalOfficesData?.data?.data?.regionalOffices ||
                                    regionalOfficesData?.data?.offices || 
                                    regionalOfficesData?.data?.regionalOffices;
                      return offices?.map((office) => (
                        <option key={office._id} value={office._id}>
                          {office.name} - {office.address}
                        </option>
                      ));
                    })()}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {(() => {
                      const offices = regionalOfficesData?.data?.data?.offices || 
                                    regionalOfficesData?.data?.data?.regionalOffices ||
                                    regionalOfficesData?.data?.offices || 
                                    regionalOfficesData?.data?.regionalOffices;
                      return offices?.length || 0;
                    })()} offices available
                  </p>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="form-section">
              <h3 className="form-section-title">Documents</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Vehicle Photo</label>
                  {existingDocuments.vehiclePhoto && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{getDocumentName(existingDocuments.vehiclePhoto)}</span>
                      <button
                        type="button"
                        onClick={() => viewDocument('vehiclePhoto')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('vehiclePhoto', e.target.files[0])}
                    className={`form-input ${isDocumentEdited('vehiclePhoto') ? 'border-2 border-blue-500' : ''}`}
                    accept="image/*"
                  />
                  {isDocumentEdited('vehiclePhoto') && (
                    <p className="mt-1 text-xs text-blue-600">New file selected</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">RC Book</label>
                  {existingDocuments.vehicleRcDoc && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{getDocumentName(existingDocuments.vehicleRcDoc)}</span>
                      <button
                        type="button"
                        onClick={() => viewDocument('vehicleRcDoc')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('vehicleRcDoc', e.target.files[0])}
                    className={`form-input ${isDocumentEdited('vehicleRcDoc') ? 'border-2 border-blue-500' : ''}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {isDocumentEdited('vehicleRcDoc') && (
                    <p className="mt-1 text-xs text-blue-600">New file selected</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Insurance</label>
                  {existingDocuments.insurance && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{getDocumentName(existingDocuments.insurance)}</span>
                      <button
                        type="button"
                        onClick={() => viewDocument('insurance')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('insurance', e.target.files[0])}
                    className={`form-input ${isDocumentEdited('insurance') ? 'border-2 border-blue-500' : ''}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {isDocumentEdited('insurance') && (
                    <p className="mt-1 text-xs text-blue-600">New file selected</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Fitness Certificate</label>
                  {existingDocuments.fitnessDoc && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{getDocumentName(existingDocuments.fitnessDoc)}</span>
                      <button
                        type="button"
                        onClick={() => viewDocument('fitnessDoc')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('fitnessDoc', e.target.files[0])}
                    className={`form-input ${isDocumentEdited('fitnessDoc') ? 'border-2 border-blue-500' : ''}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {isDocumentEdited('fitnessDoc') && (
                    <p className="mt-1 text-xs text-blue-600">New file selected</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">PUC Certificate</label>
                  {existingDocuments.pucDoc && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{getDocumentName(existingDocuments.pucDoc)}</span>
                      <button
                        type="button"
                        onClick={() => viewDocument('pucDoc')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('pucDoc', e.target.files[0])}
                    className={`form-input ${isDocumentEdited('pucDoc') ? 'border-2 border-blue-500' : ''}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {isDocumentEdited('pucDoc') && (
                    <p className="mt-1 text-xs text-blue-600">New file selected</p>
                  )}
                </div>

                {/* Permit Document - Always Optional */}
                <div className="form-group">
                  <label className="form-label">Permit Document <span className="text-gray-400">(Optional)</span></label>
                  {existingDocuments.permitDoc && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{getDocumentName(existingDocuments.permitDoc)}</span>
                      <button
                        type="button"
                        onClick={() => viewDocument('permitDoc')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('permitDoc', e.target.files[0])}
                    className={`form-input ${isDocumentEdited('permitDoc') ? 'border-2 border-blue-500' : ''}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {isDocumentEdited('permitDoc') && (
                    <p className="mt-1 text-xs text-blue-600">New file selected</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Road Tax Document</label>
                  {existingDocuments.roadTaxDoc && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{getDocumentName(existingDocuments.roadTaxDoc)}</span>
                      <button
                        type="button"
                        onClick={() => viewDocument('roadTaxDoc')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('roadTaxDoc', e.target.files[0])}
                    className={`form-input ${isDocumentEdited('roadTaxDoc') ? 'border-2 border-blue-500' : ''}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {isDocumentEdited('roadTaxDoc') && (
                    <p className="mt-1 text-xs text-blue-600">New file selected</p>
                  )}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate(`/vehicles/${id}`)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateMutation.isLoading}
                className="btn btn-primary"
              >
                {updateMutation.isLoading ? 'Updating...' : 'Update Vehicle'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Update</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to update this vehicle? This action will save all the changes you've made.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={updateMutation.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {updateMutation.isLoading ? 'Updating...' : 'Yes, Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditVehicle; 